/**
 * NetflixPlayer Component
 * Netflix-style timelapse player with:
 * - Sliding window buffering (~300 frames in memory)
 * - Double buffering (thumb200 for preview, thumb400 for playback quality)
 * - Netflix-style progress bar with hover preview
 * - Speed controls: ±1, ±6, ±12, ±24, ±50 fps
 * - Fullscreen support
 * - Hover thumbnails with persistent IndexedDB cache
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spin, Empty, Dropdown, Button, message, type MenuProps } from 'antd';
import { MaterialIcon } from '@/components/MaterialIcon';
import type { Photo } from '@/types';
import { PlayerImageDisplay } from '../PlayerImageDisplay';
import { usePlaybackController } from '../../hooks/usePlaybackController';
import { usePreloadImages } from '../../hooks/usePreloadImages';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useScrubSound } from '../../hooks/useScrubSound';
import { useHoverThumbs } from '../../hooks/useHoverThumbs';
import { SpeedButtonBar } from './SpeedButtonBar';
import { NetflixProgressBar } from './NetflixProgressBar';
import {
  DownloadFormat,
  downloadSinglePhoto,
  isMobileDevice,
} from '@/utils/downloadUtils';
import styles from './NetflixPlayer.module.css';

// Buffer window configuration
const BUFFER_WINDOW_SIZE = 300; // Frames to keep in memory around current position
const HOVER_THUMB_INTERVAL = 10; // 1 thumb every 10 frames for hover preview

export interface NetflixPlayerProps {
  photos: Photo[];
  loading: boolean;
  selectedIds: Set<string>;
  onPhotoClick: (index: number) => void;
  onSelectionToggle: (photoId: string, index: number, shiftKey: boolean) => void;
  isSelected: (photoId: string) => boolean;
  timezone?: string;
  /** Title shown as a top-left overlay on the player (default: "Preview"). */
  title?: string;
  /** Initial velocity to use on first mount (auto-play). 0 = paused. */
  initialVelocity?: number;
}

/**
 * Format timestamp for display
 */
function formatTimestamp(
  timestamp: { toDate?: () => Date } | Date | null | undefined,
  timezone?: string
): string {
  if (!timestamp) return '';
  try {
    const date = 'toDate' in timestamp && timestamp.toDate
      ? timestamp.toDate()
      : new Date(timestamp as unknown as string);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone,
    });
  } catch {
    return '';
  }
}

export function NetflixPlayer({
  photos,
  loading,
  onPhotoClick,
  timezone,
  title = "Preview",
  initialVelocity = 0,
}: NetflixPlayerProps) {
  // State
  const [velocity, setVelocity] = useState(initialVelocity);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [bufferedRanges, setBufferedRanges] = useState<Array<{ start: number; end: number }>>([]);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const velocityBeforeSeekRef = useRef(0);
  const lastPlayVelocityRef = useRef(6); // Remember last playing velocity for resume

  // Extract viewId from first photo for cache isolation
  const viewId = useMemo(() => photos[0]?.viewId, [photos]);

  // Preload images with LRU cache
  const { isImageCached, bulkPreload } = usePreloadImages(photos, 0, velocity);

  // Hover thumbnails for progress bar tooltip
  const { thumbCache: hoverThumbCache } = useHoverThumbs(photos, viewId, HOVER_THUMB_INTERVAL);

  // Playback controller
  const { state, displayIndex, jumpToIndex, stepFrame } = usePlaybackController(
    photos,
    isSeeking ? 0 : velocity, // Pause during seek
    isImageCached
  );

  // Scrub sound
  const { playTick, setEnabled: setSoundEnabled } = useScrubSound();
  const [isMuted, setIsMuted] = useState(false);
  const prevDisplayIndexRef = useRef(displayIndex);

  // Play tick sound on any frame change (playback, step, or arrow hold)
  useEffect(() => {
    if (prevDisplayIndexRef.current !== displayIndex && !isSeeking && !isMuted) {
      playTick(velocity || 1); // Use velocity for pitch, fallback to 1 for single steps
      prevDisplayIndexRef.current = displayIndex;
    }
  }, [displayIndex, velocity, isSeeking, isMuted, playTick]);

  // Stop playback when reaching either end (no looping)
  useEffect(() => {
    if (photos.length === 0) return;
    if (velocity > 0 && displayIndex >= photos.length - 1) {
      setVelocity(0);
    } else if (velocity < 0 && displayIndex <= 0) {
      setVelocity(0);
    }
  }, [displayIndex, velocity, photos.length]);

  // Toggle mute/unmute
  const handleToggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newMuted = !prev;
      setSoundEnabled(!newMuted);
      return newMuted;
    });
  }, [setSoundEnabled]);

  // Calculate buffer window and update buffered ranges
  useEffect(() => {
    if (photos.length === 0) return;

    const windowStart = Math.max(0, displayIndex - Math.floor(BUFFER_WINDOW_SIZE / 2));
    const windowEnd = Math.min(photos.length - 1, windowStart + BUFFER_WINDOW_SIZE);

    // Immediately update buffered range to the new window position
    // so the buffer indicator moves instantly on seek
    setBufferedRanges([{ start: windowStart, end: windowEnd }]);

    // Get URLs for window (prefer thumb400 for better quality, fallback to thumb200)
    // A photo whose thumbnails never made it to R2 has neither key, and is skipped.
    const urls = photos
      .slice(windowStart, windowEnd + 1)
      .map(p => p.urls.thumb400 || p.urls.thumb200)
      .filter((u): u is string => !!u);

    // Preload window in background (buffer indicator already updated)
    bulkPreload(urls).catch(() => {
      // Ignore preload errors
    });
  }, [displayIndex, photos, bulkPreload]);

  // Handle main image single click (toggle play/pause)
  const handleTogglePlay = useCallback(() => {
    setVelocity(v => {
      if (Math.abs(v) < 0.1) {
        // Resuming from a stop: if we're sitting at an end, jump to the
        // opposite boundary so playback can start again instead of being
        // immediately stopped by the end-of-stream guard.
        const next = lastPlayVelocityRef.current;
        const lastIdx = photos.length - 1;
        if (next > 0 && displayIndex >= lastIdx && photos.length > 0) {
          jumpToIndex(0);
        } else if (next < 0 && displayIndex <= 0) {
          jumpToIndex(lastIdx);
        }
        return next;
      } else {
        // Remember current velocity before stopping
        lastPlayVelocityRef.current = v;
        return 0;
      }
    });
  }, [displayIndex, photos.length, jumpToIndex]);

  // Arrow hold logic:
  // - Quick tap (<200ms): step 1 frame, stay paused
  // - Hold (>200ms): play at +50fps, stop on release
  // - If player was playing, pause first
  const HOLD_THRESHOLD_MS = 200;
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdActiveRef = useRef(false);
  const holdDirectionRef = useRef<1 | -1>(1);

  const handleArrowHoldStart = useCallback((direction: 1 | -1) => {
    // Pause if playing
    if (Math.abs(velocity) > 0.1) {
      lastPlayVelocityRef.current = velocity;
    }
    setVelocity(0);

    holdDirectionRef.current = direction;
    holdActiveRef.current = false;

    // Step 1 frame immediately
    stepFrame(direction);

    // Schedule hold activation after threshold
    holdTimerRef.current = setTimeout(() => {
      holdActiveRef.current = true;
      setVelocity(direction * 50);
    }, HOLD_THRESHOLD_MS);
  }, [velocity, stepFrame]);

  const handleArrowHoldEnd = useCallback(() => {
    // Cancel hold timer if still pending
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }

    // If hold was active (long press), stop playback
    if (holdActiveRef.current) {
      setVelocity(0);
      holdActiveRef.current = false;
    }
    // If tap (short press), already stepped 1 frame — nothing to do
  }, []);

  // Cleanup hold timer on unmount
  useEffect(() => {
    return () => {
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    };
  }, []);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onArrowHoldStart: handleArrowHoldStart,
    onArrowHoldEnd: handleArrowHoldEnd,
    onJumpToIndex: jumpToIndex,
    onTogglePlay: handleTogglePlay,
    onEscape: () => {
      if (isFullscreen) {
        handleToggleFullscreen();
      }
    },
    totalPhotos: photos.length,
    enabled: photos.length > 0,
  });

  // Handle velocity change (from speed buttons)
  const handleVelocityChange = useCallback((newVelocity: number) => {
    if (Math.abs(newVelocity) > 0.1) {
      lastPlayVelocityRef.current = newVelocity;
      // If we're sitting at an end and the user starts playing again,
      // jump to the opposite boundary so playback can actually advance.
      const lastIdx = photos.length - 1;
      if (newVelocity > 0 && displayIndex >= lastIdx && photos.length > 0) {
        jumpToIndex(0);
      } else if (newVelocity < 0 && displayIndex <= 0) {
        jumpToIndex(lastIdx);
      }
    }
    setVelocity(newVelocity);
  }, [displayIndex, photos.length, jumpToIndex]);

  // Handle seek start
  const handleSeekStart = useCallback(() => {
    velocityBeforeSeekRef.current = velocity;
    setIsSeeking(true);
  }, [velocity]);

  // Handle seek end
  const handleSeekEnd = useCallback(() => {
    setIsSeeking(false);
    // Resume playback if was playing before seek
    if (Math.abs(velocityBeforeSeekRef.current) > 0.1) {
      setVelocity(velocityBeforeSeekRef.current);
    }
  }, []);

  // Handle seek - bypass cache for Netflix-style seeking
  const handleSeek = useCallback((index: number) => {
    jumpToIndex(index, true); // bypassCache: true - jump directly to any position
  }, [jumpToIndex]);

  // Handle main image double click (open PhotoViewer, auto-pause)
  const handleMainImageDblClick = useCallback(() => {
    setVelocity(0);
    onPhotoClick(displayIndex);
  }, [onPhotoClick, displayIndex]);

  // Toggle fullscreen
  const handleToggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  }, []);

  // Listen for fullscreen changes (e.g., Esc key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);


  // Download handlers
  const handleDownload = useCallback(
    async (format: DownloadFormat) => {
      const safeIdx = Math.max(0, Math.min(displayIndex, photos.length - 1));
      const photo = photos[safeIdx];
      if (!photo) return;

      const result = await downloadSinglePhoto(photo, format, (msg) =>
        message.loading({ content: msg, key: 'download' })
      );

      if (result.success) {
        message.success({ content: 'Photo downloaded', key: 'download' });
      } else {
        message.error({ content: result.error || 'Download failed', key: 'download' });
      }
    },
    [photos, displayIndex]
  );

  const getCurrentPhotoHasRaw = useCallback(() => {
    const safeIdx = Math.max(0, Math.min(displayIndex, photos.length - 1));
    const photo = photos[safeIdx];
    return photo ? Boolean(photo.rawUrl) : false;
  }, [photos, displayIndex]);

  const showDownloadButton = !isMobileDevice();

  const getDownloadMenuItems = useCallback((): MenuProps['items'] => {
    const hasRaw = getCurrentPhotoHasRaw();
    return [
      {
        key: 'jpeg',
        label: 'JPEG',
        icon: <MaterialIcon name="image" size={18} />,
        onClick: () => handleDownload('jpeg'),
      },
      ...(hasRaw
        ? [
            {
              key: 'raw',
              label: 'RAW',
              icon: <MaterialIcon name="raw_on" size={18} />,
              onClick: () => handleDownload('raw'),
            },
            {
              key: 'both',
              label: 'Both',
              icon: <MaterialIcon name="filter_2" size={18} />,
              onClick: () => handleDownload('both'),
            },
          ]
        : []),
    ];
  }, [getCurrentPhotoHasRaw, handleDownload]);

  // Current photo
  const safeDisplayIndex = Math.max(0, Math.min(displayIndex, photos.length - 1));
  const currentPhoto = photos[safeDisplayIndex];

  // Loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  // Empty state
  if (photos.length === 0) {
    return <Empty description="No photos yet" className="py-20" />;
  }

  // Safety check
  if (!currentPhoto) {
    return <Empty description="Unable to display photo" className="py-20" />;
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.playerContainer} ${isFullscreen ? styles.fullscreen : ''}`}
    >
      {/* Top-left title overlay (e.g. "Preview") */}
      {title && (
        <div className={styles.titleOverlay}>
          {title}
        </div>
      )}
      {/* Header — stopPropagation prevents clicks from triggering play/pause */}
      <div className={styles.header} onClick={e => e.stopPropagation()}>
        <div className={styles.headerLeft}>
          <span className={styles.positionInfo}>
            {safeDisplayIndex + 1} / {photos.length.toLocaleString()}
          </span>
          <span className={styles.timestamp}>
            {formatTimestamp(currentPhoto?.capturedAt, timezone)}
          </span>
        </div>
        <div className={styles.headerRight}>
          {state.stride > 1 && (
            <span className={styles.strideInfo}>Stride: {state.stride}</span>
          )}
          {showDownloadButton && (
            <Dropdown menu={{ items: getDownloadMenuItems() }} trigger={['click']}>
              <Button
                type="text"
                icon={<MaterialIcon name="download" size={18} />}
                className={styles.iconButton}
              />
            </Dropdown>
          )}
          <Button
            type="text"
            icon={<MaterialIcon name={isMuted ? 'volume_off' : 'volume_up'} size={18} />}
            onClick={handleToggleMute}
            className={styles.iconButton}
            title={isMuted ? 'Unmute tick sound' : 'Mute tick sound'}
          />
          <Button
            type="text"
            icon={<MaterialIcon name={isFullscreen ? 'fullscreen_exit' : 'fullscreen'} size={18} />}
            onClick={handleToggleFullscreen}
            className={styles.iconButton}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          />
        </div>
      </div>

      {/* Main display */}
      <div className={styles.mainDisplay}>
        <PlayerImageDisplay
          photo={currentPhoto}
          velocity={isSeeking ? 0 : velocity}
          isImageCached={isImageCached}
          onClick={handleTogglePlay}
          onDoubleClick={handleMainImageDblClick}
          showPlayPauseOverlay
        />

        {/* Arrow navigation overlays.
            Pointer events cover both mouse and touch — the hold-to-accelerate
            gesture works on phones too, with the same +50fps behaviour as
            holding the keyboard arrow. */}
        <button
          className={`${styles.arrowOverlay} ${styles.arrowLeft}`}
          onPointerDown={(e) => {
            (e.currentTarget as HTMLButtonElement).setPointerCapture(e.pointerId);
            handleArrowHoldStart(-1);
          }}
          onPointerUp={handleArrowHoldEnd}
          onPointerCancel={handleArrowHoldEnd}
          onContextMenu={(e) => e.preventDefault()}
          title="Rewind (hold to accelerate)"
          style={{ touchAction: 'manipulation' }}
        >
          <MaterialIcon name="chevron_left" size={36} />
        </button>
        <button
          className={`${styles.arrowOverlay} ${styles.arrowRight}`}
          onPointerDown={(e) => {
            (e.currentTarget as HTMLButtonElement).setPointerCapture(e.pointerId);
            handleArrowHoldStart(1);
          }}
          onPointerUp={handleArrowHoldEnd}
          onPointerCancel={handleArrowHoldEnd}
          onContextMenu={(e) => e.preventDefault()}
          title="Forward (hold to accelerate)"
          style={{ touchAction: 'manipulation' }}
        >
          <MaterialIcon name="chevron_right" size={36} />
        </button>

        {/* Progress bar overlay — stopPropagation prevents play/pause trigger */}
        <div className={styles.progressOverlay} onClick={e => e.stopPropagation()}>
          <NetflixProgressBar
            photos={photos}
            currentIndex={safeDisplayIndex}
            bufferedRanges={bufferedRanges}
            onSeek={handleSeek}
            onSeekStart={handleSeekStart}
            onSeekEnd={handleSeekEnd}
            timezone={timezone}
            hoverThumbCache={hoverThumbCache}
            hoverThumbInterval={HOVER_THUMB_INTERVAL}
            playbackFps={Math.abs(velocity)}
          />
        </div>
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <SpeedButtonBar
          currentVelocity={velocity}
          onVelocityChange={handleVelocityChange}
          onTogglePlay={handleTogglePlay}
        />
      </div>
    </div>
  );
}
